import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { PRISMA_COMPANY_ID } from '@/constants/enums';
import type { CalendarEventType } from '@/features/calendar/entities/events';
import { useCreateEvent } from '@/features/irm/api/createEvent';
import { useDeleteEvent } from '@/features/irm/api/deleteEvent';
import { useEvents } from '@/features/irm/api/getEvents';
import { useUpdateEvent } from '@/features/irm/api/updateEvent';
import { EventSchema } from '@/features/irm/entities/events';
import { useCurrentCompanyId } from '@/hooks/useCurrentCompanyId';

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);

type EventWithCompanies = EventSchema & { company_ids?: string[] };

const dedupeEventsByTitleAndDay = (events: EventSchema[]): EventWithCompanies[] => {
  const seen = new Set<string>();
  const deduped: EventWithCompanies[] = [];

  events.forEach((event) => {
    const titleKey = (event.title || event.description || '').trim().toLowerCase();
    const date = event.start_date ? dayjs(event.start_date) : null;
    const dateKey = date?.isValid() ? date.format('YYYY-MM-DD') : '';

    const companyId = event.company_id;

    if (!titleKey || !dateKey) {
      deduped.push({
        ...event,
        company_ids: companyId ? [companyId] : undefined,
      });
      return;
    }

    const key = `${titleKey}|${dateKey}`;

    if (seen.has(key)) {
      const existingIndex = deduped.findIndex((item) => {
        const itemTitleKey = (item.title || item.description || '').trim().toLowerCase();
        const itemDate = item.start_date ? dayjs(item.start_date) : null;
        const itemDateKey = itemDate?.isValid() ? itemDate.format('YYYY-MM-DD') : '';

        return itemTitleKey === titleKey && itemDateKey === dateKey;
      });

      if (existingIndex >= 0 && companyId) {
        const existing = deduped[existingIndex];
        const existingCompanies = existing.company_ids ?? (existing.company_id ? [existing.company_id] : []);
        if (!existingCompanies.includes(companyId)) {
          deduped[existingIndex] = {
            ...existing,
            company_ids: [...existingCompanies, companyId],
          };
        }
      }
      return;
    }

    seen.add(key);
    deduped.push({
      ...event,
      company_ids: companyId ? [companyId] : undefined,
    });
  });

  return deduped;
};

export type AdminEvent = {
  id: string;
  title: string;
  description?: string;
  dayIndex: number;
  startHour: number;
  startMinute: number;
  durationMinutes: number;
  allDay?: boolean;
  categoryId?: string;
  local?: string;
  favorite?: boolean;
  hide?: boolean;
  start_date?: string;
  end_date?: string;
  before_market?: 'time' | 'before_market' | 'after_market' | 'full_day';
  company_id?: string;
  user?: {
    name: string;
    image_url?: string;
  };
  impact?: {
    id: number;
    name: string;
  };
  event_type?: {
    id: number;
    name: string;
  };
  origin?: string;
  shareholder?: {
    name: string;
    quantity: number;
  };
  isMultiDay?: boolean;
  is_created_by_admin?: boolean;
};

type UseCalendarEventsOptions = {
  selectedImpacts?: number[];
  isAdmin?: boolean;
  companyIdOverride?: string;
};

export const useCalendarEvents = (options: UseCalendarEventsOptions = {}) => {
  const { selectedImpacts = [], isAdmin = false, companyIdOverride } = options;
  const currentCompanyId = useCurrentCompanyId();
  const companyId = companyIdOverride ?? currentCompanyId;

  const shouldFetchAllEvents = !isAdmin;

  const eventsQueryAdmin = useEvents({
    companyId,
    filters: {
      page: 1,
      size: 100000,
      is_created_by_admin: true,
    },
    config: {
      enabled: shouldFetchAllEvents,
    },
  });

  const eventsQueryClient = useEvents({
    companyId,
    filters: {
      page: 1,
      size: 1000,
      is_created_by_admin: false,
    },
    config: {
      enabled: shouldFetchAllEvents,
    },
  });

  const eventsQuery = useEvents({
    companyId,
    filters: {
      page: 1,
      size: 1000,
      is_created_by_admin: isAdmin ? true : undefined,
    },
    config: {
      enabled: !shouldFetchAllEvents,
    },
  });

  const allEvents = isAdmin
    ? eventsQuery.data?.data.events || []
    : [
        ...(eventsQueryAdmin.data?.data.events || []),
        ...(eventsQueryClient.data?.data.events || []),
      ].filter((event, index, self) => index === self.findIndex((e) => e.id === event.id));

  const filteredEvents = allEvents.filter((event) => {
    // Filtrar eventos por company_id: mostrar apenas eventos da empresa do usuário
    // Não mostrar eventos com PRISMA_COMPANY_ID para usuários não-admin
    const matchesCompany = isAdmin
      ? true
      : event.company_id === companyId && event.company_id !== PRISMA_COMPANY_ID;

    const matchesImpact = selectedImpacts.length === 0 || selectedImpacts.includes(event.impact.id);

    return matchesCompany && matchesImpact;
  });

  const dedupedEvents = dedupeEventsByTitleAndDay(filteredEvents);

  const events: CalendarEventType[] = dedupedEvents.map((event) => {
    const isFullDayEvent =
      event.before_market === 'full_day' ||
      event.before_market === 'before_market' ||
      event.before_market === 'after_market';

    const startDate = isFullDayEvent
      ? dayjs(event.start_date.split('T')[0]).local().locale('pt-br')
      : dayjs.utc(event.start_date).local().locale('pt-br');

    const endDate = event.end_date
      ? isFullDayEvent
        ? dayjs(event.end_date.split('T')[0]).local().locale('pt-br')
        : dayjs.utc(event.end_date).local().locale('pt-br')
      : undefined;

    return {
      ...event,
      title: event.title || event.description,
      start_date: startDate,
      end_date: endDate,
      local: event.local || '',
      company_ids: event.company_ids ?? (event.company_id ? [event.company_id] : undefined),
    };
  });

  const irmEventsAsAdminEvents: AdminEvent[] = dedupedEvents.map((event) => {
    const isFullDayEvent =
      event.before_market === 'full_day' ||
      event.before_market === 'before_market' ||
      event.before_market === 'after_market';

    const eventDate = isFullDayEvent
      ? dayjs(event.start_date.split('T')[0]).local().locale('pt-br')
      : dayjs.utc(event.start_date).local().locale('pt-br');

    const weekStart = dayjs().startOf('week').add(1, 'day');
    const dayIndex = eventDate.diff(weekStart, 'day');

    let startHour = eventDate.hour();
    let startMinute = eventDate.minute();

    let durationMinutes = 60;
    if (event.end_date) {
      const endDate = isFullDayEvent
        ? dayjs(event.end_date.split('T')[0])
        : dayjs.utc(event.end_date).local();
      durationMinutes = Math.max(1, endDate.diff(eventDate, 'minute'));
    }

    const isFullDay =
      event.before_market === 'full_day' ||
      event.before_market === 'before_market' ||
      event.before_market === 'after_market';

    const isMultiDay =
      event.end_date && !dayjs(event.end_date.split('T')[0]).isSame(eventDate, 'day');

    if (isFullDay) {
      startHour = -1;
      startMinute = 0;
      durationMinutes = 0;
    }

    return {
      id: event.id,
      title: event.title || event.description || 'Evento IRM',
      description: event.description || '',
      dayIndex: Math.max(0, dayIndex),
      startHour,
      startMinute,
      durationMinutes,
      allDay:
        event.before_market === 'before_market' ||
        event.before_market === 'after_market' ||
        event.before_market === 'full_day',
      local: event.local || '',
      favorite: event.favorite || false,
      hide: event.hide || false,
      date: eventDate.format('YYYY-MM-DD'),
      start_date: eventDate.format('YYYY-MM-DDTHH:mm:ss'),
      end_date: event.end_date
        ? isFullDayEvent
          ? dayjs(event.end_date.split('T')[0]).format('YYYY-MM-DDTHH:mm:ss')
          : dayjs.utc(event.end_date).local().format('YYYY-MM-DDTHH:mm:ss')
        : undefined,
      before_market: event.before_market,
      company_id: event.company_id,
      user: event.user || { name: 'Sistema', image_url: '' },
      impact: event.impact || { id: 1, name: 'Alto' },
      event_type: event.event_type || { id: 1, name: 'Evento' },
      origin: event.origin || 'IRM',
      shareholder: event.shareholder
        ? {
            name: event.shareholder.name || '',
            quantity: event.shareholder.quantity,
          }
        : undefined,
      is_created_by_admin: event.is_created_by_admin,
      isMultiDay: isMultiDay || false,
      company_ids: event.company_ids ?? (event.company_id ? [event.company_id] : undefined),
    };
  });

  const createEventMutation = useCreateEvent({
    config: {
      onSuccess: () => {
        eventsQuery.refetch();
        if (shouldFetchAllEvents) {
          eventsQueryAdmin.refetch();
          eventsQueryClient.refetch();
        }
      },
    },
  });

  const updateEventMutation = useUpdateEvent({
    config: {
      onSuccess: () => {
        eventsQuery.refetch();
        if (shouldFetchAllEvents) {
          eventsQueryAdmin.refetch();
          eventsQueryClient.refetch();
        }
      },
    },
  });

  const deleteEventMutation = useDeleteEvent({
    config: {
      onSuccess: () => {
        eventsQuery.refetch();
        if (shouldFetchAllEvents) {
          eventsQueryAdmin.refetch();
          eventsQueryClient.refetch();
        }
      },
    },
  });

  const createEvent = async (eventData: Omit<CalendarEventType, 'id'>) => {
    return createEventMutation.mutate({
      companyId,
      title: eventData.title,
      description: eventData.description,
      local: eventData.local,
      start_date: eventData.start_date.format('YYYY-MM-DD'),
      end_date: eventData.end_date?.format('YYYY-MM-DD'),
      impact_id: eventData.impact.id,
      event_type_id: eventData.event_type.id,
      shareholders: [],
    });
  };

  const updateEvent = async (eventData: CalendarEventType) => {
    return updateEventMutation.mutate({
      id: eventData.id,
      companyId,
      title: eventData.title,
      description: eventData.description,
      local: eventData.local,
      start_date: eventData.start_date.format('YYYY-MM-DD'),
      end_date: eventData.end_date?.format('YYYY-MM-DD'),
      impact_id: eventData.impact.id,
      event_type_id: eventData.event_type.id,
      shareholders: [],
    });
  };

  const deleteEvent = async (eventId: string) => {
    return deleteEventMutation.mutate({
      id: eventId,
      companyId,
    });
  };

  const isLoading = isAdmin
    ? eventsQuery.isLoading
    : eventsQueryAdmin.isLoading || eventsQueryClient.isLoading;

  const refetch = () => {
    if (isAdmin) {
      return eventsQuery.refetch();
    }
    return Promise.all([eventsQueryAdmin.refetch(), eventsQueryClient.refetch()]);
  };

  return {
    events,
    irmEventsAsAdminEvents,
    isLoading,
    createEvent,
    updateEvent,
    deleteEvent,
    refetch,
  };
};
